import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo: errorInfo.componentStack || "" });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: "" });
  };

  handleHome = () => {
    window.location.hash = "#/";
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
          {/* Animated background */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-red-500/5 blur-3xl animate-pulse" />
            <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-brand-500/5 blur-3xl animate-pulse" />
          </div>

          <div className="relative w-full max-w-lg text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20 shadow-lg shadow-red-500/5">
              <AlertTriangle className="h-10 w-10 text-red-400 animate-pulse" />
            </div>

            <h1 className="text-2xl font-bold text-surface-100 mb-2">
              Something went wrong
            </h1>
            <p className="text-surface-400 text-sm mb-6">
              An unexpected error occurred. You can try reloading the page or
              returning to the dashboard.
            </p>

            {this.state.error && (
              <div className="mb-6 rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-left">
                <p className="text-sm font-mono text-red-300 break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 rounded-lg border border-surface-600 bg-surface-800 px-5 py-2.5 text-sm font-medium text-surface-200 transition-all hover:bg-surface-700 hover:border-surface-500"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
              <button
                onClick={this.handleHome}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-brand-500 to-purple-500 px-5 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/25"
              >
                <Home className="h-4 w-4" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
